import { Box, Group, rem, Stack, Text } from '@mantine/core'
import { useCallback, useEffect, useState } from 'react'
import { getBoggleGameModeDefinition } from '~/boggle/gameModes'
import {
  ChallengeButton,
  IconButton,
  PrimaryButton,
  SecondaryButton,
} from '~/boggle/ui/atoms/Buttons'
import { StandardBestScoreHeadingRow } from '~/boggle/ui/chrome/AppLayout'
import { CompendiumTrophyButton } from '~/boggle/ui/compendium/CompendiumTrophyButton'
import { GameModeHowToPlayModal } from '~/boggle/ui/modes/GameModeHowToPlayModal'
import { SiteHeroHeading } from '~/boggle/ui/screens/SiteHeroHeading'
import { STANDARD_STAGING_PRIMARY_WIDTH } from '~/boggle/ui/screens/standardStagingConstants'
import { getBoggleProgressRepository } from '~/boggle/progress/webProgressRepository'

const LANDING_HUB_REFERENCE_MODE_ID = 'race' as const
const LANDING_HUB_HARD_MODE_ID = 'race-hard' as const
const GOLD_OR_BETTER_RUN_RATINGS = new Set(['gold', 'diamond', 'ace'])

/** Hub landing; primary standard run starts in-place on `/`; challenge/playground wired by route. */
export function HomeScreen({
  isDevMode,
  onOpenChallenge,
  onOpenPlayground,
  onStartHardRun,
  onStartStandardRun,
}: {
  isDevMode: boolean
  onOpenChallenge: () => void
  onOpenPlayground: () => void
  onStartHardRun: () => void
  onStartStandardRun: () => void
}) {
  const copy = getBoggleGameModeDefinition(LANDING_HUB_REFERENCE_MODE_ID)
  const hardMode = getBoggleGameModeDefinition(LANDING_HUB_HARD_MODE_ID)
  const [standardBest, setStandardBest] = useState<null | number>(null)
  const [hasGoldRatedRun, setHasGoldRatedRun] = useState(false)
  const [howToOpen, setHowToOpen] = useState(false)
  const closeHowTo = useCallback(() => setHowToOpen(false), [])

  useEffect(() => {
    let cancelled = false

    const repository = getBoggleProgressRepository()
    void Promise.all([
      repository.getStandardBestScore(copy.id),
      repository.getCompletedRuns(),
    ])
      .then(([bestScore, runs]) => {
        if (!cancelled) {
          setStandardBest(bestScore)
          setHasGoldRatedRun(
            runs.some(run => GOLD_OR_BETTER_RUN_RATINGS.has(run.ratingId)),
          )
        }
      })
      .catch(() => {
        if (!cancelled) {
          setStandardBest(null)
          setHasGoldRatedRun(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [copy.id])

  return (
    <Stack
      component="article"
      px={30}
      align="center"
      gap={0}
      h="100%"
      mih={0}
      pos="relative"
    >
      <GameModeHowToPlayModal definition={howToOpen ? copy : null} onClose={closeHowTo} />
      <Box pos="absolute" right={14} top={14} style={{ zIndex: 2 }}>
        <CompendiumTrophyButton testId="boggle-home-compendium-trophy" />
      </Box>
      <SiteHeroHeading />
      <Stack
        align="center"
        gap={12}
        justify="flex-end"
        mb="max(env(safe-area-inset-bottom, 0px), 30px)"
        miw={0}
        mt="auto"
        pb={40}
        w={STANDARD_STAGING_PRIMARY_WIDTH}
      >
        {/* {standardBest === null ? (
          <SecondaryButton
            data-testid="boggle-landing-how-to-play"
            fz={18}
            h={46}
            mt={0}
            onClick={() => setHowToOpen(true)}
            variant="subtle"
            w="80%"
            ff="Tilt Neon, sans-serif"
            c="rgba(255, 255, 255, 0.8)"
            pb={3}
            // mb={40}
          >
            How to play
          </SecondaryButton>
        ) : null} */}
        <Stack gap={6} w="100%" align="center">
          {standardBest !== null ? (
            <StandardBestScoreHeadingRow bestScore={standardBest} headerTrailing />
          ) : null}
          <Group pos="relative" w="100%" mt={standardBest !== null ? 10 : 0}>
            <PrimaryButton
              bd={`2px solid ${copy.color}`}
              data-testid="boggle-landing-games-button"
              onClick={onStartStandardRun}
              fz={28}
              w="100%"
            >
              Play
            </PrimaryButton>

            {/* {standardBest !== null ? (
              <IconButton
                aria-label="How to play"
                data-testid="boggle-landing-how-to-play-fab"
                onClick={() => setHowToOpen(true)}
                size={56}
                style={{
                  top: '50%',
                  position: 'absolute',
                  right: rem(-66),
                  transform: 'translateY(-50%)',
                }}
              >
                <Text component="span" ff="Tilt Neon, sans-serif" fz={24} lh={1}>
                  ?
                </Text>
              </IconButton>
            ) : null} */}
          </Group>
        </Stack>
        {/* {isDevMode ? (
          <SecondaryButton onClick={onOpenPlayground} w="100%">
            Playground
          </SecondaryButton>
        ) : null} */}

        {/* <ChallengeButton
          onClick={onOpenChallenge}
          w="100%"
          style={{ boxShadow: '0 0 10px 1px rgba(255, 200, 200, 0.3)' }}
        >
          {copy.challenge.buttonLabel}
        </ChallengeButton> */}
      </Stack>
    </Stack>
  )
}
